import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './login.css';

// Importações do Font Awesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faSpinner } from '@fortawesome/free-solid-svg-icons';

// Importações do Firebase e do nosso Contexto de Autenticação
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from '../Config/firebase';
import { useAuth } from "../Context/auth"; // Importamos o useAuth

function Login() {
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [showSenha, setShowSenha] = useState(false);
    const [mensagem, setMensagem] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Usamos o hook para verificar se o usuário já está logado
    const { logado, loginError, setLoginError, signOutUser, retrySession, appUserData } = useAuth(); // <--- Destructure

    // Add login-page class to body for background styling
    useEffect(() => {
        document.body.classList.add('login-page');
        return () => {
            document.body.classList.remove('login-page');
        };
    }, []);

    // Efeito para redirecionar se o usuário já estiver logado
    useEffect(() => {


        if (logado && appUserData) {
            navigate('/app/home');
        }
    }, [logado, appUserData, navigate]);

    // Efeito para mostrar erro de login vindo do Contexto (ex: Limite de licenças)
    useEffect(() => {
        if (loginError) {
            setLoading(false); // Garante que o loading pare se houver erro vindo do contexto
            if (typeof loginError === 'object' && loginError.code === 'SESSION_CONFLICT') {
                const Swal = require('sweetalert2');
                Swal.fire({
                    title: 'Sessão Ativa Detectada',
                    text: loginError.message || "Usuário já conectado. Deseja desconectar a outra sessão e entrar aqui?",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Sim, desconectar outros',
                    cancelButtonText: 'Cancelar'
                }).then((result) => {
                    if (result.isConfirmed) {
                        retrySession(true);
                    } else {
                        signOutUser();
                        setMensagem("Login cancelado pelo usuário.");
                        setLoginError(null);
                    }
                });
            } else if (typeof loginError === 'object' && loginError.code === 'SUBSCRIPTION_EXPIRED') {
                // --- ERRO DE ASSINATURA VENCIDA (COM LINK) ---
                const Swal = require('sweetalert2');
                Swal.fire({
                    title: 'Acesso Negado',
                    html: `
                        <p>${loginError.message}</p>
                        <hr/>
                        <p style="margin-bottom: 0;">Precisa de ajuda?</p>
                        <a href="https://wa.me/553799826905?text=Ola,%20minha%20assinatura%20venceu%20e%20preciso%20renovar." target="_blank" style="text-decoration: none; color: #25D366; font-weight: bold; display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 10px;">
                            <i class="fab fa-whatsapp" style="font-size: 1.5rem;"></i>
                            Falar com Suporte
                        </a>
                    `,
                    icon: 'error',
                    confirmButtonText: 'Entendi',
                    confirmButtonColor: '#d33'
                }).then(() => {
                    signOutUser();
                    setLoginError(null);
                });
            } else {
                // Erro normal (string ou objeto sem code conflict)
                const msg = typeof loginError === 'string' ? loginError : loginError.message;
                setMensagem(msg);
                setLoginError(null); // Limpa o erro
            }
        }
    }, [loginError, setLoginError, retrySession, signOutUser]);

    useEffect(() => {
        if (location.state?.email) {
            setEmail(location.state.email);
        }

        // Mensagem via Redirect (state)
        if (location.state?.message) {
            setSuccessMessage(location.state.message);
            window.history.replaceState({}, document.title);
        }

        // Mensagem via SessionStorage (Interceptor Global)
        const sessionMsg = sessionStorage.getItem('loginMessage');
        if (sessionMsg) {
            setMensagem(sessionMsg); // Exibe como erro (vermelho)
            sessionStorage.removeItem('loginMessage');
        }
    }, [location.state]);

    // ======================= FUNÇÃO DE LOGIN ATUALIZADA =======================
    async function handleLogin(e) {
        e.preventDefault();
        setMensagem('');

        if (!email || !senha) {
            setMensagem('Por favor, informe e-mail e senha.');
            return;
        }

        setLoading(true);

        try {
            // Flag para permitir que o AuthContext crie uma nova sessão
            sessionStorage.setItem('isLoggingIn', 'true');
            localStorage.removeItem('sessionId'); // Remove sessão antiga para evitar conflitos/erros

            // A única responsabilidade do Login é autenticar com o servidor do Firebase.
            // O listener onAuthStateChanged (auth.jsx) irá detectar o login, 
            // buscar o contexto e fazer o redirecionamento.
            await signInWithEmailAndPassword(auth, email, senha);

            // O redirecionamento ocorrerá automaticamente via useEffect quando 'logado' mudar para true.

        } catch (error) {
            setLoading(false); // Paramos o loading apenas em caso de erro
            sessionStorage.removeItem('isLoggingIn');
            // Tratamento de erros de autenticação do Firebase
            if (error.code === 'auth/invalid-credential') {
                setMensagem('E-mail ou senha incorretos.');
            } else {
                setMensagem(error.message || "Ocorreu um erro ao tentar fazer login.");
                console.error("Erro de login:", error);
            }
        }
        // finally retirado para que o loading continue até o unmount/redirect em caso de sucesso
    }
    // ============================ FIM DA ATUALIZAÇÃO ============================

    return (
        <div className="d-flex align-items-center text-center form-container">
            <form className="form-signin" onSubmit={handleLogin}>
                <img className="mb-4" src="/imagens/logo_fundo_branco_pequena.png" alt="Logo R6 ERP" />
                <h1 className="h3 mb-3 fw-normal">Login</h1>

                {successMessage && <div className="alert alert-success" role="alert">{successMessage}</div>}

                <div className="form-group text-start mb-3">
                    <label htmlFor="emailInput" className="form-label">E-mail</label>
                    <input
                        onChange={(e) => setEmail(e.target.value)}
                        type="email"
                        className="form-control"
                        id="emailInput"
                        placeholder="seu@email.com"
                        value={email}
                        required
                    />
                </div>

                <div className="form-group text-start mb-3">
                    <label htmlFor="passwordInput" className="form-label">Senha</label>
                    <div className="position-relative">
                        <input
                            onChange={(e) => setSenha(e.target.value)}
                            type={showSenha ? 'text' : 'password'}
                            className="form-control"
                            id="passwordInput"
                            placeholder="Sua senha"
                            value={senha}
                            required
                        />
                        <button
                            type="button"
                            className="btn btn-password-toggle"
                            onClick={() => setShowSenha(!showSenha)}
                        >
                            <FontAwesomeIcon icon={showSenha ? faEyeSlash : faEye} />
                        </button>
                    </div>
                </div>
                <div className="text-start mb-3">
                    <Link to="/app/resetsenha" className="text-decoration-none small" style={{ color: '#2185c6' }}>
                        Esqueci minha senha
                    </Link>
                </div>

                <button
                    className="btn btn-primary w-100 py-2"
                    type="submit"
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                            Entrando...
                        </>
                    ) : (
                        'Entrar'
                    )}
                </button>

                {mensagem && <div className="alert alert-danger mt-3" role="alert">{mensagem}</div>}

                <div className="mt-4 text-center">
                    <p className="mb-2 text-muted fw-bold">Ainda não é cliente?</p>
                    <Link to="/app/novaconta" className="btn btn-success w-100 py-2 fw-bold shadow-sm btn-create-account">
                        Se cadastre agora e teste grátis por 7 dias.
                    </Link>
                </div>

                <p className="mt-5 mb-3 text-body-secondary">&copy; Desenvolvido por R6 Software</p>
            </form>
        </div>
    );
}

export default Login;